import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MessageCircle, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api } from "../../lib/api";

const inputCls = "w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm";
const EMPTY = { extract_id: "", first_name: "", last_name: "", email: "", phone: "", transaction_ref: "", amount: "" };

export const waLink = (sale) => {
  const phone = (sale.phone || "").replace(/[^\d]/g, "");
  const msg = `Bonjour ${sale.first_name}, merci pour votre achat ! Voici votre exemplaire personnel de « ${sale.title} » (lecture en ligne ou téléchargement) : ${sale.link}`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};

export const DeliverEbookDialog = ({ order, extracts, open, onOpenChange, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sale, setSale] = useState(null);
  useEffect(() => {
    if (!open) return;
    setSale(null);
    const [first = "", ...rest] = (order?.customer_name || "").split(" ");
    setForm({ ...EMPTY, extract_id: order?.extract_id || "", first_name: first, last_name: rest.join(" "), phone: order?.customer_phone || "", amount: order?.amount ?? "" });
  }, [open, order]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, order_id: order?.id || null, phone: form.phone || null, amount: form.amount === "" ? null : Number(form.amount) };
      const { data } = await api.post("/admin/ebook-sales", payload, { withCredentials: true });
      setSale(data);
      toast[data.email_status === "envoye" ? "success" : "warning"](data.email_status === "envoye" ? "Livre envoyé par e-mail" : "Exemplaire créé, mais l'e-mail n'est pas parti : envoyez le lien par WhatsApp");
      onDone?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Vérifiez les champs (e-mail valide, tous les champs requis)");
    } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div><label className="text-xs font-body text-[#4A4A4A] mb-1 block">{label}</label><input value={form[k]} onChange={set(k)} data-testid={`deliver-${k}-input`} className={inputCls} {...props} /></div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deliver-ebook-dialog" className="bg-[#FAF9F6] rounded-none border-[#D1CFC7] max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-left">Livrer le livre numérique</DialogTitle>
          <DialogDescription className="font-body text-sm text-[#4A4A4A] text-left">{order ? `Commande ${order.ref}` : "Vente directe"} — une copie marquée au nom de l'acheteur sera créée puis envoyée par e-mail.</DialogDescription>
        </DialogHeader>
        {sale ? <SaleResult sale={sale} /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="text-xs font-body text-[#4A4A4A] mb-1 block">Livre *</label>
              <select value={form.extract_id} onChange={set("extract_id")} data-testid="deliver-extract-input" className={inputCls}>
                <option value="">— Choisir —</option>
                {extracts.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
            </div>
            {field("first_name", "Prénom *")}
            {field("last_name", "Nom *")}
            {field("email", "E-mail *", { type: "email" })}
            {field("phone", "WhatsApp", { placeholder: "+237…" })}
            {field("transaction_ref", "N° de transaction *")}
            {field("amount", "Montant (F CFA)", { type: "number", min: 0 })}
            <button onClick={submit} disabled={busy} data-testid="deliver-submit" className="sm:col-span-2 bg-[#2C402E] text-[#FAF9F6] py-3 text-sm font-body flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin" />} Valider et envoyer
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SaleResult = ({ sale }) => (
  <div data-testid="deliver-result" className="space-y-3 font-body text-sm">
    <p>Exemplaire de <strong>{sale.first_name} {sale.last_name}</strong> prêt ({sale.format.toUpperCase()}). E-mail : <strong data-testid="deliver-email-status">{sale.email_status === "envoye" ? "envoyé" : "non envoyé"}</strong>.</p>
    <input readOnly value={sale.link} data-testid="deliver-link" className={inputCls} onFocus={(e) => e.target.select()} />
    <div className="flex flex-wrap gap-2">
      <button onClick={() => navigator.clipboard.writeText(sale.link).then(() => toast.success("Lien copié"))} data-testid="deliver-copy-link" className="border border-[#1A1A1A] px-4 py-2 text-xs flex items-center gap-2"><Copy size={13} /> Copier le lien</button>
      <a href={waLink(sale)} target="_blank" rel="noreferrer" data-testid="deliver-whatsapp" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-xs flex items-center gap-2"><MessageCircle size={13} /> Envoyer par WhatsApp</a>
    </div>
  </div>
);
